import { ErrorNotice } from '../../components/ErrorNotice.js';
import { useEffect, useRef, useState } from 'react';
import { acceptTerms, getConsentStatus, getTerms, type Terms } from '../../lib/api/endpoints.js';

interface Props {
  children: React.ReactNode;
  /**
   * Called with the licence once the terms have loaded — the one place it is
   * fetched, so the app never shows or writes a licence the server hasn't
   * confirmed, and a failure has one error and one Retry, not two.
   */
  onLicence?: (licence: string) => void;
}

/**
 * Blocks contribution until the contributor has accepted the current terms.
 *
 * This gate is not a formality and it is not a cookie banner. VoxFauna does not
 * own these recordings — contributors do — and without a stored, versioned
 * grant from each of them the corpus cannot lawfully be published at all.
 * Retrofitting consent across thousands of people, some of whom have long since
 * stopped using the app, is not a project that finishes.
 *
 * So the terms are shown **in full, in the app**, at a moment when the person
 * is paying attention — not linked from a footer and not folded into a
 * catch-all Terms of Service. Contributors are licensing their recordings into
 * a public research corpus, and being paid for by commercial licensees. That
 * needs to be an explicit, informed moment.
 */
export function ConsentGate({ children, onLicence }: Props) {
  const [state, setState] = useState<
    { phase: 'loading' } | { phase: 'blocked'; terms: Terms; renewal: boolean } | { phase: 'ok' } | { phase: 'error'; error: unknown }
  >({ phase: 'loading' });
  // A ref, so a parent passing a fresh callback each render can't re-trigger the load.
  const onLicenceRef = useRef(onLicence);
  onLicenceRef.current = onLicence;
  const [accepting, setAccepting] = useState(false);
  const [readToEnd, setReadToEnd] = useState(false);

  const load = () => {
    setState({ phase: 'loading' });
    Promise.all([getConsentStatus(), getTerms()])
      .then(([status, terms]) => {
        onLicenceRef.current?.(terms.licence);
        if (status.hasAccepted && status.isCurrent) {
          setState({ phase: 'ok' });
        } else {
          setState({ phase: 'blocked', terms, renewal: status.hasAccepted });
        }
      })
      .catch((e) => setState({ phase: 'error', error: e }));
  };

  useEffect(load, []);

  if (state.phase === 'loading') return <p className="meta">Loading…</p>;

  if (state.phase === 'error') {
    return <ErrorNotice error={state.error} title="We couldn't load the contributor terms." onRetry={load} />;
  }

  if (state.phase === 'ok') return <>{children}</>;

  const accept = async () => {
    setAccepting(true);
    try {
      await acceptTerms(state.terms.version);
      setState({ phase: 'ok' });
    } catch (e) {
      setState({ phase: 'error', error: e });
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div>
      <p className="eyebrow">{state.renewal ? 'Updated terms' : 'Before you contribute'}</p>
      <h1 className="page-title">
        {state.renewal ? 'These terms have changed' : 'What happens to your recordings'}
      </h1>
      <p className="page-intro">
        {state.renewal
          ? 'Please read the current version. Recordings you have already contributed stay under the terms you accepted at the time.'
          : 'Please read this properly. It is short, and some of it is not what people expect.'}
      </p>

      <div
        className="terms-body"
        onScroll={(e) => {
          const el = e.currentTarget;
          // Enabling the button only after the text has been scrolled through
          // is a small friction with a real purpose: it makes "I read it" less
          // of a fiction, and this is a document with genuine consequences.
          if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setReadToEnd(true);
        }}
      >
        <Markdown source={state.terms.body} />
      </div>

      <div className="stack" style={{ marginTop: '1.5rem' }}>
        <button type="button" className="btn" disabled={!readToEnd || accepting} onClick={accept}>
          {accepting ? 'Saving…' : 'I understand — I agree'}
        </button>
        <span className="meta">
          {readToEnd ? `Version ${state.terms.version}` : 'Please read to the end'}
        </span>
      </div>
    </div>
  );
}

/**
 * A deliberately tiny Markdown renderer.
 *
 * The terms are served by the API as Markdown so a revision needs no client
 * release. Pulling in a full Markdown library — and its sanitiser — for one
 * trusted document is not worth the bundle, so this handles the four
 * constructs the terms actually use and escapes everything else.
 *
 * If the terms ever grow tables or links, replace this rather than extending
 * it. Hand-rolled Markdown parsers are how HTML injection happens.
 */
function Markdown({ source }: { source: string }) {
  const blocks = source.split('\n\n');

  return (
    <>
      {blocks.map((block, i) => {
        const trimmed = block.trim();

        if (trimmed.startsWith('## ')) return <h2 key={i}>{trimmed.slice(3)}</h2>;
        if (trimmed.startsWith('# ')) return <h1 key={i}>{trimmed.slice(2)}</h1>;

        if (trimmed.startsWith('- ')) {
          return (
            <ul key={i}>
              {trimmed.split('\n').map((line, j) => (
                <li key={j}>
                  <Bold text={line.replace(/^-\s*/, '')} />
                </li>
              ))}
            </ul>
          );
        }

        return (
          <p key={i}>
            <Bold text={trimmed} />
          </p>
        );
      })}
    </>
  );
}

/** Renders **bold** runs. Everything else is plain text, by construction. */
function Bold({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
        part.startsWith('**') && part.endsWith('**') ? (
          <strong key={i}>{part.slice(2, -2)}</strong>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}
